/**
 * Preferência de idioma e de tema sobrevivendo ao F5.
 *
 * 🔴 As duas vêm no token e eram aplicadas SÓ depois da resposta do login. Num F5 o app subia com o
 * default do bootstrap (`scheme: 'light'` e o idioma do navegador), e a preferência real chegava
 * depois — o usuário via o tema claro piscar e a logo montar no idioma errado antes de trocar.
 * Persistir aqui é o que deixa a PRIMEIRA pintura já sair certa; o token continua sendo a fonte da
 * verdade e sobrescreve isto no login.
 *
 * ⚠️ Leitura falha fechado: valor ausente, corrompido ou fora do conjunto aceito devolve
 * `undefined`, e quem chama decide o default. Idioma inválido gravado aqui deixaria o Transloco
 * buscando um bundle que não existe, e `scheme` inválido quebraria o tema.
 *
 * ⚠️ `localStorage` lança em modo privado de alguns navegadores e quando a cota estoura. Nenhuma
 * destas funções pode derrubar o bootstrap por causa de uma preferência de tela.
 */
const LANGUAGE_STORAGE_KEY = 'hmax-order:language';
const SCHEME_STORAGE_KEY = 'hmax-order:scheme';
const ACCEPTED_SCHEMES = ['auto', 'dark', 'light'] as const;

export type StoredScheme = (typeof ACCEPTED_SCHEMES)[number];

function read(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Preferência de tela não justifica derrubar nada: segue sem persistir.
  }
}

/**
 * Idioma gravado, desde que esteja entre os que a aplicação carrega. `acceptedLanguages` vem do
 * `availableLangs` do Transloco: é ele que decide o que é válido, não esta função.
 */
export function readStoredLanguage(acceptedLanguages: string[]): string | undefined {
  const language = read(LANGUAGE_STORAGE_KEY);

  return language && acceptedLanguages.includes(language) ? language : undefined;
}

export function storeLanguage(language: string): void {
  write(LANGUAGE_STORAGE_KEY, language);
}

export function readStoredScheme(): StoredScheme | undefined {
  const scheme = read(SCHEME_STORAGE_KEY);

  return ACCEPTED_SCHEMES.includes(scheme as StoredScheme) ? (scheme as StoredScheme) : undefined;
}

export function storeScheme(scheme: string): void {
  write(SCHEME_STORAGE_KEY, scheme);
}

/**
 * Apaga as duas preferências. Chamado no logout: a escolha é do usuário, não do navegador, e sem
 * isto o próximo a entrar no mesmo aparelho herdaria idioma e tema do anterior.
 */
export function clearStoredPreferences(): void {
  try {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    localStorage.removeItem(SCHEME_STORAGE_KEY);
  } catch {
    // Mesmo motivo da escrita: nada aqui pode derrubar o logout.
  }
}
